import { useState, useEffect, useRef } from 'react'
import { isAxiosError } from 'axios'
import { usePasswordlessRequest, usePasswordlessVerify } from '@/api/hooks/auth.hooks'
import { getErrorMessage } from '@/api/hooks/useMutationWithToast'

export type UiMessage = { title: string; description: string; severity: 'success' | 'warning' | 'error' }
export type AuthStep = 'email' | 'otp'

/**
 * What the failure means for the page, beyond its message. `no-account` and
 * `email-taken` are the two the pages turn into a cross-link: the visitor is on
 * the wrong one of /login and /signup and needs to be handed to the other.
 */
export type AuthErrorKind = 'no-account' | 'email-taken' | 'rate-limited' | 'generic'

/** Which call failed. The two steps map the same status codes differently —
 * `passwordless/request` 404s for an unknown account, `passwordless/verify`
 * 404s for a bad code. */
export type AuthPhase = 'request' | 'verify'

/** NestJS validation errors arrive as `message: string[]`, everything else as a string. */
function errorText(e: unknown): string {
  const raw: unknown = getErrorMessage(e)
  return Array.isArray(raw) ? raw.join(' ') : String(raw)
}

export function mapPasswordlessApiError(e: unknown, phase: AuthPhase = 'verify'): UiMessage & { kind: AuthErrorKind } {
  const status = isAxiosError(e) ? e.response?.status : undefined
  const msg = errorText(e).toLowerCase()

  if (status === 429)
    return { kind: 'rate-limited', title: 'Too Many Attempts', description: 'Too many attempts, please wait a moment.', severity: 'warning' }

  if (phase === 'request') {
    if (status === 404)
      return { kind: 'no-account', title: 'No Account Found', description: 'No account found for this email. Create an account to continue.', severity: 'warning' }
    if (status === 409)
      return { kind: 'email-taken', title: 'Email Already Registered', description: 'This email address is already registered. Sign in instead.', severity: 'warning' }
    // The signup route validates its body, so a 400 carries the field error worth showing.
    if (status === 400)
      return { kind: 'generic', title: 'Check Your Details', description: errorText(e), severity: 'error' }
  }

  if (msg.includes('code expired')) return { kind: 'generic', title: 'Verification Code Expired', description: 'Your code has expired. Request a new code and try again.', severity: 'warning' }
  if (msg.includes('invalid code')) return { kind: 'generic', title: 'Incorrect Verification Code', description: 'The code entered is incorrect. Double-check and try again.', severity: 'error' }
  return { kind: 'generic', title: 'Something Went Wrong', description: "We couldn't complete your request. Please try again.", severity: 'error' }
}

type Options = {
  onSuccess?: (accessToken: string) => void | Promise<void>
  initialEmail?: string
  /**
   * The first step: what sends the code. Defaults to `POST /auth/passwordless/request`;
   * the signup page injects `POST /auth/signup` instead. Resending is deliberately
   * *not* routed through this — by then the account exists, so a resend always goes
   * to `passwordless/request` (signing up again would 409).
   */
  requestCode?: (email: string) => Promise<unknown>
  /** Pending flag for an injected `requestCode`. */
  isRequestingCode?: boolean
  /**
   * Whether an `initialEmail` immediately sends a code and jumps to the OTP step.
   * Login does; signup only prefills, since it still needs a name and the consents.
   */
  autoRequestCode?: boolean
}

export function usePasswordlessAuth({
  onSuccess,
  initialEmail,
  requestCode: injectedRequestCode,
  isRequestingCode: injectedIsRequesting,
  autoRequestCode = true,
}: Options = {}) {
  const autoRequesting = Boolean(initialEmail) && autoRequestCode
  const [step, setStep] = useState<AuthStep>(autoRequesting ? 'otp' : 'email')
  const [email, setEmail] = useState(initialEmail ?? '')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [errorKind, setErrorKind] = useState<AuthErrorKind | null>(null)
  const [notice, setNotice] = useState<UiMessage | null>(null)
  const [cooldownRemaining, setCooldownRemaining] = useState(0)
  const [isResending, setIsResending] = useState(false)

  const requestCode = usePasswordlessRequest()
  const verify = usePasswordlessVerify()

  const fail = (err: unknown, phase: AuthPhase) => {
    const mapped = mapPasswordlessApiError(err, phase)
    setError(mapped.description)
    setErrorKind(mapped.kind)
  }

  const clearMessages = () => {
    setError(null)
    setErrorKind(null)
    setNotice(null)
  }

  const isCooldownActive = cooldownRemaining > 0
  useEffect(() => {
    if (!isCooldownActive) return
    const id = setInterval(() => {
      setCooldownRemaining((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => clearInterval(id)
  }, [isCooldownActive])

  const requestedRef = useRef(false)
  const { mutate } = requestCode
  useEffect(() => {
    if (!autoRequesting || !initialEmail || requestedRef.current) return
    requestedRef.current = true
    setEmail(initialEmail)
    setStep('otp')
    mutate(
      { email: initialEmail },
      { onError: (err: unknown) => fail(err, 'request') }
    )
  }, [autoRequesting, initialEmail, mutate])

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearMessages()
    try {
      if (injectedRequestCode) await injectedRequestCode(email)
      else await requestCode.mutateAsync({ email })
      setStep('otp')
    } catch (err) {
      fail(err, 'request')
    }
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleVerifyCode = async () => {
    clearMessages()
    setIsSubmitting(true)
    try {
      const result = await verify.mutateAsync({ email, code: otp })
      await onSuccess?.(result.accessToken)
    } catch (err) {
      fail(err, 'verify')
      setOtp('')
      setIsSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    clearMessages()
    setIsResending(true)
    try {
      await requestCode.mutateAsync({ email })
      setNotice({ title: 'New Code Sent', description: "We've sent a new 6-digit code to your email.", severity: 'success' })
      setOtp('')
      setCooldownRemaining(30)
    } catch (err) {
      fail(err, 'request')
    } finally {
      setIsResending(false)
    }
  }

  const handleBackToEmail = () => {
    setStep('email')
    setOtp('')
    clearMessages()
  }

  return {
    step, email, otp, error, errorKind, notice, cooldownRemaining,
    setEmail, setOtp,
    handleEmailSubmit, handleVerifyCode, handleResendCode, handleBackToEmail,
    isRequestingCode: injectedIsRequesting ?? requestCode.isPending,
    isVerifying: isSubmitting,
    isResending,
  }
}
